import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { Check, FileText, Loader2, Pencil, Plus, Search, Trash2, X } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { EmptyState, Skeleton } from '@/components/ui/misc'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { CollapsiblePanel } from '@/components/ui/collapsible-panel'
import { DocumentActions } from './document-actions'
import { DocumentMetadataDialog } from './document-metadata-dialog'
import { BulkDrop, IngestBar, UploadingRow, useIngest } from './ingest-panel'
import { DocumentText } from './document-text'
import { deleteDocument, getDocuments, type DocumentItem } from '@/lib/api/documents/get-documents'
import { useDocumentTypeLabel } from '@/lib/document-types'
import { apiErrorMessage, traceIdOf } from '@/lib/api-client'
import { cn, compareStrings, formatNumber } from '@/lib/utils'
import { useLocalizedPath } from '@/lib/i18n/routes'
import type { Collection } from '@/types/terms'

type DocSort = 'newest' | 'title' | 'terms'

interface DocumentsViewProps {
  collection: Collection | undefined
  /** Free-text query from the shared header search. */
  query: string
  /** The open document — the last of `selectedIds`. */
  selectedId: number | null
  /** Opens one document on its own (null closes and clears the selection). */
  onSelect: (id: number | null) => void
  /** Checked documents, in selection order; they are also the document filter. */
  selectedIds: number[]
  onSelectionChange: (ids: number[]) => void
  /** When arriving from a mention: the term to pick out and the page to open at. */
  focusTerm?: string
  focusPage?: number | null
  /** Switch the workbench to the terms view. */
  onShowTerms?: () => void
}

export function DocumentsView({
  collection,
  query,
  selectedId,
  onSelect,
  selectedIds,
  onSelectionChange,
  focusTerm,
  focusPage,
  onShowTerms,
}: DocumentsViewProps) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const path = useLocalizedPath()
  const typeLabel = useDocumentTypeLabel()
  const cid = collection?.id ?? null
  // Viewers can look but not touch; the backend enforces this too.
  const canEdit = collection?.my_role === 'owner' || collection?.my_role === 'editor'

  const [sort, setSort] = useState<DocSort>('newest')
  const [type, setType] = useState('')
  const [lang, setLang] = useState('')
  const [domain, setDomain] = useState('')
  const [domainFilter, setDomainFilter] = useState('')
  const [validOnly, setValidOnly] = useState(false)
  const [extracted, setExtracted] = useState('')  // '' | 'yes' | 'no'
  const [editing, setEditing] = useState<number | null>(null)
  const [confirming, setConfirming] = useState<number | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  // The selection lives in the URL (it is the document filter on the other
  // tabs), so this component only computes the next value.
  const selected = selectedIds
  const setSelected = onSelectionChange
  // Where a shift-click range starts: the last document clicked or ticked.
  const anchorRef = useRef<number | null>(null)

  const toggleSelected = (id: number) => {
    anchorRef.current = id
    setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id])
  }

  /**
   * Row click with the usual list conventions: plain click opens that one
   * document (again on the open one closes it), ⌘/Ctrl toggles it in the
   * selection, Shift adds the range from the last clicked row. The clicked
   * row goes last so it is the one the text pane shows.
   */
  const clickRow = (id: number, e: React.MouseEvent, visible: DocumentItem[]) => {
    if (e.shiftKey && anchorRef.current !== null) {
      const ids = visible.map((d) => d.id)
      const a = ids.indexOf(anchorRef.current)
      const b = ids.indexOf(id)
      if (a >= 0 && b >= 0) {
        const range = ids.slice(Math.min(a, b), Math.max(a, b) + 1)
        const kept = selected.filter((x) => !range.includes(x))
        const ordered = a <= b ? range : [...range].reverse()
        setSelected([...kept, ...ordered])
        return
      }
    }
    if (e.metaKey || e.ctrlKey) {
      toggleSelected(id)
      return
    }
    anchorRef.current = id
    onSelect(selected.length === 1 && selectedId === id ? null : id)
  }

  /**
   * Adding documents here means "these belong in this collection" — the target
   * is already chosen, which is the only question the preview's commit step
   * was asking. So this ingests directly, one file or fifty.
   *
   * The preview flow keeps its own purpose: try one document, look at what it
   * yields, tune the prompt, and only then decide. It stays reachable from the
   * nav, and the empty state points at it for exactly that case.
   */
  const ingest = useIngest(cid)
  const startUpload = (files: File[]) => ingest.start(files)

  /** Try a single document without committing anything. */
  const startPreview = (file: File) =>
    navigate({ pathname: path('preview'), search: `?kogu=${cid ?? ''}` }, { state: { file } })

  const docs = useQuery({
    queryKey: ['documents', cid],
    queryFn: () => getDocuments(cid),
    enabled: cid !== null,
    // While files are arriving or processing, the list is the progress display:
    // each poll is a row appearing or a badge changing. Idle, it never polls.
    refetchInterval: ingest.busy ? 2000 : false,
  })

  const types = useMemo(
    () =>
      [...new Set((docs.data ?? []).map((d) => d.document_type || 'other'))].sort(),
    [docs.data],
  )

  /** Split a comma-separated field into trimmed, non-empty values. */
  const codes = (s: string | null | undefined) =>
    (s ?? '').split(',').map((v) => v.trim()).filter(Boolean)

  /** Only the domains present in this collection, not all 122 in the term base —
   *  offering a domain that matches nothing here would be a dead option. */
  const domains = useMemo(() => {
    const all = [...new Set((docs.data ?? []).flatMap((d) => codes(d.domains)))].sort()
    const f = domainFilter.trim().toLowerCase()
    return (f ? all.filter((d) => d.toLowerCase().includes(f)) : all).slice(0, 200)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs.data, domainFilter])

  /** Codes actually present in this collection, rather than a fixed et/en/fi —
   *  a document's `languages` is detected from its text and may be anything. */
  const langs = useMemo(
    () =>
      [
        ...new Set(
          (docs.data ?? []).flatMap((d) =>
            (d.languages ?? '').split(',').map((l) => l.trim()).filter(Boolean),
          ),
        ),
      ].sort(),
    [docs.data],
  )

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    let list = (docs.data ?? []).filter((d) => {
      if (type && (d.document_type || 'other') !== type) return false
      // Match against the parsed code list, not the raw string: "en,et" has to
      // match a filter on either language, and a substring test would let "et"
      // match a hypothetical "eth".
      if (lang && !codes(d.languages).includes(lang)) return false
      if (domain && !codes(d.domains).includes(domain)) return false
      if (validOnly && !d.is_valid) return false
      if (extracted === 'yes' && !d.terms_extracted_at) return false
      if (extracted === 'no' && d.terms_extracted_at) return false
      if (!q) return true
      return (
        d.title.toLowerCase().includes(q) ||
        (d.author ?? '').toLowerCase().includes(q) ||
        (d.publisher ?? '').toLowerCase().includes(q)
      )
    })
    const cmp: Record<DocSort, (a: DocumentItem, b: DocumentItem) => number> = {
      newest: (a, b) => b.id - a.id,
      title: (a, b) => compareStrings(a.title, b.title),
      terms: (a, b) => b.term_count - a.term_count,
    }
    return [...list].sort(cmp[sort])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs.data, query, type, lang, domain, validOnly, extracted, sort])

  const remove = useMutation({
    mutationFn: (id: number) => deleteDocument(id),
    onSuccess: (_r, id) => {
      // Deleting a document removes its mentions, so the term base moved too.
      qc.invalidateQueries({ queryKey: ['documents', cid] })
      qc.invalidateQueries({ queryKey: ['terms'] })
      qc.invalidateQueries({ queryKey: ['collections'] })
      if (selected.includes(id)) setSelected(selected.filter((x) => x !== id))
      setConfirming(null)
      toast.success(t('documents.deleted'))
    },
    onError: (e) => {
      const tid = traceIdOf(e)
      toast.error(t('documents.deleteFailed'), {
        description: apiErrorMessage(e) + (tid ? ` · ${t('common.traceId', { id: tid })}` : ''),
      })
    },
  })

  if (cid === null) return <EmptyState icon={FileText} title={t('collections.select')} />

  return (
    <>
      {/* Same skeleton as the Terminid mode: filters, list, detail. */}
      <CollapsiblePanel
        id="doc-filters"
        title={t('common.filters')}
        activeCount={
          [type, lang, domain, extracted].filter(Boolean).length + (validOnly ? 1 : 0)
        }
      >
        <div className="flex h-full flex-col gap-4 overflow-y-auto p-3 scrollbar-thin">
        {/* Language first, mirroring the Terms filter panel — the two modes
            share a pane, so a filter that jumps position between them reads as
            a different control. Hidden when a collection is monolingual: a
            toggle whose every option returns the same list is just noise. */}
        {langs.length > 1 && (
          <div>
            <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {t('filters.language')}
            </div>
            <ToggleGroup
              type="single"
              value={lang || 'all'}
              onValueChange={(v) => setLang(!v || v === 'all' ? '' : v)}
              className="w-full"
            >
              <ToggleGroupItem value="all" className="flex-1">
                {t('common.all')}
              </ToggleGroupItem>
              {langs.map((l) => (
                <ToggleGroupItem key={l} value={l} className="flex-1 uppercase">
                  {l}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        )}

        <div>
          <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {t('common.sortBy')}
          </div>
          <ToggleGroup
            type="single"
            value={sort}
            onValueChange={(v) => v && setSort(v as DocSort)}
            className="w-full"
          >
            <ToggleGroupItem value="newest" className="flex-1">
              {t('documents.sortNewest')}
            </ToggleGroupItem>
            <ToggleGroupItem value="title" className="flex-1">
              {t('common.alphabetical')}
            </ToggleGroupItem>
            <ToggleGroupItem value="terms" className="flex-1">
              {t('documents.sortTerms')}
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div>
          <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {t('documents.extraction')}
          </div>
          <ToggleGroup
            type="single"
            value={extracted || 'all'}
            onValueChange={(v) => setExtracted(!v || v === 'all' ? '' : v)}
            className="w-full"
          >
            <ToggleGroupItem value="all" className="flex-1">
              {t('common.all')}
            </ToggleGroupItem>
            <ToggleGroupItem
              value="yes"
              className="flex-1"
              title={t('documents.extractionDoneHint')}
            >
              {t('documents.extractionDone')}
            </ToggleGroupItem>
            <ToggleGroupItem
              value="no"
              className="flex-1"
              title={t('documents.extractionPendingHint')}
            >
              {t('documents.extractionPending')}
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <label className="flex cursor-pointer items-center gap-2 text-xs">
          <Checkbox checked={validOnly} onCheckedChange={(v) => setValidOnly(v === true)} />
          {t('documents.validOnly')}
        </label>

        {/* Same searchable-list treatment as the Terms panel: a plain <select>
            cannot cope with this many domains, and the two panes share a pane
            so the control should behave identically in both. */}
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {t('filters.domain')}
            </span>
            {domain && (
              <Button variant="ghost" size="icon-sm" onClick={() => setDomain('')}>
                <X />
              </Button>
            )}
          </div>
          <div className="relative mb-1.5">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value)}
              placeholder={t('filters.searchDomain')}
              className="h-7 pl-7 text-xs"
            />
          </div>
          <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto rounded-md border border-border">
            {domains.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDomain(d === domain ? '' : d)}
                className={cn(
                  'block w-full truncate px-2 py-1 text-left text-[11px] transition-colors',
                  d === domain
                    ? 'bg-accent font-medium text-accent-foreground'
                    : 'hover:bg-muted',
                )}
                title={d}
              >
                {d}
              </button>
            ))}
            {!domains.length && (
              <p className="p-2 text-[11px] text-muted-foreground">{t('common.noMatches')}</p>
            )}
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {t('documents.type')}
            </span>
            {type && (
              <Button variant="ghost" size="icon-sm" onClick={() => setType('')}>
                <X />
              </Button>
            )}
          </div>
          <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto rounded-md border border-border">
            {types.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setType(v === type ? '' : v)}
                className={cn(
                  'block w-full truncate px-2 py-1 text-left text-[11px] transition-colors',
                  v === type ? 'bg-accent font-medium text-accent-foreground' : 'hover:bg-muted',
                )}
              >
                {typeLabel(v)}
              </button>
            ))}
          </div>
        </div>

        <div className="shrink-0 border-t border-border pt-2 text-[11px] text-muted-foreground">
          <Badge variant="primary">{formatNumber(visible.length)}</Badge> /{' '}
          {formatNumber(docs.data?.length ?? 0)}
        </div>
        </div>
      </CollapsiblePanel>

      <section className="flex w-[420px] shrink-0 flex-col border-r border-border">
        <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-1.5">
          {canEdit && (
            <Checkbox
              checked={visible.length > 0 && visible.every((d) => selected.includes(d.id))}
              onCheckedChange={(v) =>
                // Only ever touches what is currently visible, so a filtered-out
                // document can never be swept into a bulk action unseen.
                setSelected(v === true ? visible.map((d) => d.id) : [])
              }
              title={t('documents.selectAllVisible')}
            />
          )}
          <span className="text-[11px] text-muted-foreground">
            {t('units.documents', { count: visible.length + ingest.uploads.length })}
          </span>
          {canEdit && (
            <>
              {/* Always reachable, unlike the empty-state dropzone which
                  disappears as soon as a document is selected. */}
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,.pdf"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? [])
                  e.target.value = ''
                  if (files.length) startUpload(files)
                }}
              />
              <Button
                variant="outline"
                size="sm"
                className="ml-auto"
                onClick={() => fileRef.current?.click()}
              >
                <Plus />
                {t('documents.add')}
              </Button>
            </>
          )}
        </div>

        {canEdit && (
          <DocumentActions
            selected={selected}
            collectionId={cid}
            onClearSelection={() => setSelected([])}
          />
        )}

        {/* Only renders once a batch exists; the idle drop target lives in the
            empty state, and the button above covers the non-empty case. */}
        {/* Shown while a batch runs, and afterwards for as long as there are
            terms left to group — that second condition is a fact about the
            collection, so it survives a reload and a backend restart, which
            the "a batch just finished" version did not. */}
        {(ingest.visible || (!!collection?.unchecked_count && !!canEdit)) && (
          <IngestBar
            ingest={ingest}
            canEdit={!!canEdit}
            collectionId={cid}
            unchecked={collection?.unchecked_count ?? 0}
            onShowTerms={onShowTerms}
          />
        )}

        {docs.isLoading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : !visible.length && !ingest.uploads.length ? (
          <EmptyState
            icon={FileText}
            title={
              query || type ? t('common.noMatchesFound') : t('documents.emptyCollection')
            }
          />
        ) : (
          <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
            {/* Files still in the browser, shaped like the rows they are about
                to become — so a drop reads as one sequence rather than files
                vanishing and documents appearing somewhere else later. */}
            {ingest.uploads.map((u) => (
              <UploadingRow key={u.key} row={u} />
            ))}
            {visible.map((d) => (
              <div
                key={d.id}
                onClick={(e) => clickRow(d.id, e, visible)}
                // Shift-click would otherwise drag-select the titles as text.
                onMouseDown={(e) => e.shiftKey && e.preventDefault()}
                className={cn(
                  'group cursor-pointer border-b border-border/60 px-3 py-2 transition-colors',
                  d.id === selectedId
                    ? 'bg-accent'
                    : selected.includes(d.id)
                      ? 'bg-accent/50 hover:bg-accent/70'
                      : 'hover:bg-muted',
                )}
              >
                <div className="flex items-start gap-2">
                  {canEdit && (
                    <div onClick={(e) => e.stopPropagation()} className="pt-0.5">
                      <Checkbox
                        checked={selected.includes(d.id)}
                        onCheckedChange={() => toggleSelected(d.id)}
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p
                      className={cn(
                        'truncate text-[13px]',
                        d.id === selectedId ? 'font-semibold' : 'font-medium',
                      )}
                    >
                      {d.title}
                    </p>
                    <p className="truncate text-[11px] text-muted-foreground">
                      {[d.author, d.publication_year, d.publisher]
                        .filter(Boolean)
                        .join(' · ') || t('documents.noMetadata')}
                    </p>
                  </div>
                  {canEdit && (
                    <div className="flex shrink-0 gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          setEditing(d.id)
                        }}
                        title={t('documents.editMetadata')}
                      >
                        <Pencil />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          setConfirming(d.id)
                        }}
                        title={t('documents.deleteDocument')}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="mt-1 flex items-center gap-2 text-[10px] text-muted-foreground">
                  {d.document_type && d.document_type !== 'other' && (
                    <Badge variant="outline" size="sm">
                      {typeLabel(d.document_type)}
                    </Badge>
                  )}
                  {!d.is_valid && (
                    <Badge variant="warning" size="sm">
                      {t('documents.invalid')}
                    </Badge>
                  )}
                  {/* State and yield are now separate facts: extraction
                      runs before indexing, so a document can have its terms
                      while the search index is still being built. Showing
                      only one of the two would hide whichever the reader
                      happened to want. */}
                  {d.current_state === 'processing' &&
                    (ingest.byDocument.get(d.id)?.status === 'running' ? (
                      <Badge variant="primary" size="sm" title={t('documents.processingHint')}>
                        <Loader2 className="size-3 animate-spin" />
                        {t(d.terms_extracted_at ? 'documents.indexingPct' : 'documents.processingPct', {
                          pct: ingest.byDocument.get(d.id)?.percentage ?? 0,
                        })}
                      </Badge>
                    ) : (
                      <Badge variant="default" size="sm" title={t('documents.queuedHint')}>
                        {t('documents.queued')}
                      </Badge>
                    ))}
                  {d.current_state === 'failed' && (
                    <Badge variant="danger" size="sm" title={t('documents.processingFailedHint')}>
                      {t('documents.processingFailed')}
                    </Badge>
                  )}
                  {d.terms_extracted_at ? (
                    d.term_count === 0 ? (
                      // Processed, just barren. Same tick, no colour: the run
                      // finished, which is what the mark means — it is not a
                      // verdict on the yield.
                      <span
                        className="flex items-center gap-1"
                        title={t('documents.noTermsFoundHint')}
                      >
                        <Check className="size-3 text-muted-foreground" />
                        {t('documents.noTermsFound')}
                      </span>
                    ) : (
                      <span
                        className="flex items-center gap-1 tabular-nums"
                        title={t('documents.extractionDoneHint')}
                      >
                        <Check className="size-3 text-success" />
                        {t('units.terms', { count: d.term_count })}
                      </span>
                    )
                  ) : d.current_state === 'uploaded' ? (
                    <Badge variant="warning" size="sm" title={t('documents.extractionPendingHint')}>
                      {t('documents.notExtracted')}
                    </Badge>
                  ) : null}
                  <span className="tabular-nums">
                    {t('units.chunks', { count: d.chunk_count })}
                  </span>
                </div>

                {confirming === d.id && (
                  <div
                    className="mt-2 rounded border border-danger/30 bg-danger/10 p-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <p className="mb-1.5 text-[11px] text-danger">
                      {t('documents.deleteWarning')}
                    </p>
                    <div className="flex gap-1.5">
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(d.id)}
                      >
                        {remove.isPending ? (
                          <Loader2 className="animate-spin" />
                        ) : (
                          t('common.delete')
                        )}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* One dialog for the whole list; which document it edits is state. */}
      <DocumentMetadataDialog
        docId={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          qc.invalidateQueries({ queryKey: ['documents', cid] })
        }}
      />

      <section className="flex min-w-0 flex-1 flex-col">
        {selectedId ? (
          <DocumentText
            documentId={selectedId}
            collectionId={cid}
            canEdit={!!canEdit}
            jumpTerm={focusTerm}
            jumpPage={focusPage}
            onEdit={() => setEditing(selectedId)}
            onClose={() => onSelect(null)}
          />
        ) : (
          <EmptyWorkbench
            canEdit={!!canEdit}
            onUpload={startUpload}
            onPreview={startPreview}
          />
        )}
      </section>
    </>
  )
}

/**
 * Nothing selected: instead of a blank pane, offer the one thing you would
 * want here — adding documents. Dropping ingests straight into the collection,
 * however many files; the link underneath covers the other intent, trying a
 * single document before committing to it.
 */
function EmptyWorkbench({
  canEdit,
  onUpload,
  onPreview,
}: {
  canEdit: boolean
  onUpload: (files: File[]) => void
  onPreview: (file: File) => void
}) {
  const { t } = useTranslation()
  const previewRef = useRef<HTMLInputElement>(null)
  if (!canEdit) {
    return (
      <EmptyState
        icon={FileText}
        title={t('documents.selectDocument')}
        hint={t('documents.selectDocumentHint')}
      />
    )
  }
  return (
    <div className="flex h-full items-center justify-center p-8">
      <div className="w-full max-w-lg space-y-3">
        <div className="text-center">
          <h3 className="text-sm font-medium">{t('documents.selectOrAdd')}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('documents.selectOrAddHint')}
          </p>
        </div>
        <BulkDrop busy={false} onFiles={onUpload} />
        <input
          ref={previewRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0]
            e.target.value = ''
            if (f) onPreview(f)
          }}
        />
        <p className="text-center text-[11px] text-muted-foreground">
          {t('documents.orTryOne')}{' '}
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => previewRef.current?.click()}
          >
            {t('documents.tryOneLink')}
          </button>
        </p>
      </div>
    </div>
  )
}
